import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { ICON_SIZE } from '../../../constants/icons';
import { adminUploadMedia } from '../../lib/api';
import { DMC_OPTIONS, MDA_OPTIONS } from '../../lib/mdaRegistry';
import { ArticleHeroMedia } from '../../components/article/ArticleHeroMedia';
import { ARTICLE_MEDIA_VIDEO_FILE_ACCEPT } from '../../../../shared/constants/articleMedia';
import {
  DEFAULT_ARTICLE_HERO_MEDIA,
  normalizeArticleHeroMediaSize,
  type ArticleHeroMediaAlign,
  type ArticleHeroMediaKind,
  type ArticleHeroMediaSize,
  type ArticleHeroMediaVerticalAlign,
  type ArticleHeroMedia as ArticleHeroMediaValue,
} from '../../../../shared/domain/articleHeroMedia';

import type { ArticleType } from '../../../../shared/schemas/article';

interface ArticleHeroMediaEditorProps {
  heroMedia: ArticleHeroMediaValue;
  onHeroMediaChange: (value: ArticleHeroMediaValue) => void;
  contentType?: ArticleType;
}

export function ArticleHeroMediaEditor({
  heroMedia,
  onHeroMediaChange,
  contentType = 'article',
}: ArticleHeroMediaEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const demoOptions = useMemo(
    () => [...MDA_OPTIONS, ...DMC_OPTIONS],
    [],
  );

  const updateHeroMedia = useCallback((patch: Partial<ArticleHeroMediaValue>) => {
    onHeroMediaChange({ ...heroMedia, ...patch });
  }, [heroMedia, onHeroMediaChange]);

  const handleKindChange = useCallback((kind: ArticleHeroMediaKind) => {
    if (kind === 'none') {
      onHeroMediaChange({ ...DEFAULT_ARTICLE_HERO_MEDIA });
      return;
    }

    onHeroMediaChange({
      kind,
      demoId: kind === 'demo' ? heroMedia.demoId ?? demoOptions[0]?.id : undefined,
      url: kind === 'image' || kind === 'video' ? heroMedia.url : undefined,
      posterUrl: kind === 'video' ? heroMedia.posterUrl : undefined,
      size: heroMedia.size,
      align: heroMedia.align,
      verticalAlign: heroMedia.verticalAlign,
    });
  }, [demoOptions, heroMedia, onHeroMediaChange]);

  const handleUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadError(null);
    setUploading(true);

    try {
      const result = await adminUploadMedia(file);
      onHeroMediaChange({
        kind: result.kind,
        url: result.url,
        posterUrl: result.posterUrl,
        size: heroMedia.size,
        align: heroMedia.align,
        verticalAlign: heroMedia.verticalAlign,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setUploadError(message);
      console.error('[error] [admin:articleHeroMedia:upload]', { message });
    } finally {
      setUploading(false);
    }
  }, [heroMedia.align, heroMedia.verticalAlign, heroMedia.size, onHeroMediaChange]);

  return (
    <div className="flex flex-col gap-4 rounded-card border border-border-default bg-surface-panel p-4">
      <div className="flex flex-col gap-2">
        <span className="text-label text-text-quaternary">Hero media</span>
        <p className="text-btn text-text-tertiary">
          Choose a mini demo, uploaded image, or uploaded video for the block above the{' '}
          {contentType === 'feature' ? 'feature' : 'article'} body.
        </p>
      </div>

      <FieldGroup label="Media type">
        <select
          value={heroMedia.kind}
          onChange={(e) => handleKindChange(e.target.value as ArticleHeroMediaKind)}
          className="rounded-comfortable border border-border-default bg-surface-base px-3 py-3 text-caption-lg text-text-primary focus:border-border-focus focus:outline-none"
        >
          <option value="none">None</option>
          <option value="demo">Mini demo app</option>
          <option value="image">Static image</option>
          <option value="video">Video</option>
        </select>
      </FieldGroup>

      {heroMedia.kind === 'demo' && (
        <FieldGroup label="Mini demo app">
          <select
            value={heroMedia.demoId ?? ''}
            onChange={(e) => updateHeroMedia({ demoId: e.target.value || undefined })}
            className="rounded-comfortable border border-border-default bg-surface-base px-3 py-3 text-caption-lg text-text-primary focus:border-border-focus focus:outline-none"
          >
            <option value="">Select a demo</option>
            {demoOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </FieldGroup>
      )}

      {(heroMedia.kind === 'image' || heroMedia.kind === 'video') && (
        <div className="flex flex-col gap-3">
          <FieldGroup label="Upload file">
            <input
              ref={fileInputRef}
              type="file"
              accept={heroMedia.kind === 'image' ? 'image/*' : ARTICLE_MEDIA_VIDEO_FILE_ACCEPT}
              onChange={handleUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-ghost flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Upload size={ICON_SIZE.sm} />
              {uploading ? 'Optimizing upload…' : `Upload ${heroMedia.kind}`}
            </button>
            {uploadError && (
              <p className="text-caption text-status-error">{uploadError}</p>
            )}
            {heroMedia.url && (
              <p className="break-all text-tiny text-text-quaternary">{heroMedia.url}</p>
            )}
          </FieldGroup>

          <FieldGroup label="Or paste media URL">
            <input
              type="url"
              value={heroMedia.url ?? ''}
              onChange={(e) => updateHeroMedia({ url: e.target.value || undefined })}
              className="rounded-comfortable border border-border-default bg-surface-base p-3 text-caption-lg text-text-primary placeholder:text-text-empty focus:border-border-focus focus:outline-none"
              placeholder="https://..."
            />
          </FieldGroup>
        </div>
      )}

      {heroMedia.kind !== 'none' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FieldGroup label="Zoom">
            <select
              value={normalizeArticleHeroMediaSize(heroMedia.size)}
              onChange={(e) => updateHeroMedia({ size: e.target.value as ArticleHeroMediaSize })}
              className="rounded-comfortable border border-border-default bg-surface-base px-3 py-3 text-caption-lg text-text-primary focus:border-border-focus focus:outline-none"
            >
              <option value="100">100% (original)</option>
              <option value="125">125% zoom in</option>
              <option value="150">150% zoom in</option>
              <option value="200">200% zoom in</option>
            </select>
          </FieldGroup>

          <FieldGroup label="Horizontal">
            <select
              value={heroMedia.align}
              onChange={(e) => updateHeroMedia({ align: e.target.value as ArticleHeroMediaAlign })}
              className="rounded-comfortable border border-border-default bg-surface-base px-3 py-3 text-caption-lg text-text-primary focus:border-border-focus focus:outline-none"
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </FieldGroup>

          <FieldGroup label="Vertical">
            <select
              value={heroMedia.verticalAlign}
              onChange={(e) => updateHeroMedia({
                verticalAlign: e.target.value as ArticleHeroMediaVerticalAlign,
              })}
              className="rounded-comfortable border border-border-default bg-surface-base px-3 py-3 text-caption-lg text-text-primary focus:border-border-focus focus:outline-none"
            >
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </FieldGroup>
        </div>
      )}

      <FieldGroup label="Site preview">
        <div className="rounded-card border border-border-subtle bg-surface-base p-4">
          {heroMedia.kind === 'none' ? (
            <p className="text-caption text-text-empty">No hero media selected.</p>
          ) : (
            <ArticleHeroMedia heroMedia={heroMedia} />
          )}
        </div>
      </FieldGroup>
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label text-text-quaternary">{label}</span>
      {children}
    </div>
  );
}
